import React, { useState, useEffect } from 'react';
import { Table, InputNumber, Select, Button, Popconfirm, Card, Tooltip, DatePicker } from 'antd';
import { Dayjs } from 'dayjs';
import { PlusOutlined, DeleteOutlined, InfoCircleOutlined, CopyOutlined } from '@ant-design/icons';
import { NonResourcePricingTable } from '../interfaces/interfaces';
import type { ColumnsType } from 'antd/es/table';
import { useCurrency } from '../../context/CurrencyContext';

const { Option } = Select;

interface NonResourceCostsProps {
  dataSource: NonResourcePricingTable[];
  onChange: (data: NonResourcePricingTable[]) => void;
  nonResourcePricingDetailsData: any;
  startDate: Dayjs;
  endDate: Dayjs;
  viewOnly?: boolean;
}

// Build a Region -> Service Line -> [Functions] lookup tree from the catalog
const transformData_ = (data: any): Record<string, any> => {
  if (!data) return {};
  const groupedData: Record<string, any> = {};

  data.forEach((item: { region: string; service_line: string; function: string }) => {
    const { region, service_line, function: func } = item;
    if (!groupedData[region]) groupedData[region] = {};
    if (!groupedData[region][service_line]) groupedData[region][service_line] = [];
    if (!groupedData[region][service_line].includes(func)) {
      groupedData[region][service_line].push(func);
    }
  });

  return groupedData;
};

// Target Price/Month = monthly cost / (1 - margin%). Guards margin >= 100.
const calcTargetPricePerMonth = (monthlyCost: number, margin: number): number => {
  const denom = 100 - margin;
  return denom > 0 ? Number(((monthlyCost * 100) / denom).toFixed(2)) : 0;
};

const NonResourceCosts: React.FC<NonResourceCostsProps> = ({ dataSource: initialData, onChange, nonResourcePricingDetailsData, startDate, endDate, viewOnly = false }) => {
  const { currencySymbol } = useCurrency();
  const [dataSource, setDataSource] = useState<NonResourcePricingTable[]>(initialData);
  const [options_, setOptions_] = useState(transformData_(nonResourcePricingDetailsData));

  useEffect(() => {
    setDataSource(initialData);
  }, [initialData]);

  useEffect(() => {
    setOptions_(transformData_(nonResourcePricingDetailsData));
  }, [nonResourcePricingDetailsData]);

  useEffect(() => {
    onChange(dataSource);
  }, [dataSource]);

  const columns: ColumnsType<NonResourcePricingTable> = [
    {
      title: '',
      key: 'rowNumber',
      align: 'center' as const,
      width: 48,
      render: (_: any, __: NonResourcePricingTable, index: number) => (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 28,
            height: 28,
            padding: '0 8px',
            borderRadius: 6,
            border: '1px solid #d6e4ff',
            backgroundColor: '#f0f5ff',
            color: '#4096FF',
            fontSize: 12,
            fontWeight: 600,
            fontVariantNumeric: 'tabular-nums',
            flexShrink: 0,
          }}
        >
          {index + 1}
        </span>
      ),
    },
    {
      dataIndex: 'action',
      render: (_: any, record: NonResourcePricingTable) =>
        dataSource.length >= 1 ? (
          <div style={{ width: '75px' }}>
            <Popconfirm title="Sure to delete?" onConfirm={() => handleDelete(record.key)}>
              <Button icon={<DeleteOutlined />} style={{ color: '#F3797E' }} disabled={viewOnly} />
            </Popconfirm>
            <Button className="ms-2" onClick={() => handleDuplicate(record)} disabled={viewOnly} icon={<CopyOutlined />} style={{ color: '#6FBE73' }} />
          </div>
        ) : null,
    },
    {
      title: 'Region',
      dataIndex: 'selectRegion',
      render: (_: string, record: NonResourcePricingTable) => (
        <Select allowClear value={record.selectRegion || undefined} style={{ width: 150 }} onChange={(value) => handleSelectChange(value, record.key, 'selectRegion')} disabled={viewOnly}>
          {Object.keys(options_).map((item: string) => (
            <Option key={`${record.key}-${item}-rg`} value={item}>{item}</Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Service Line',
      dataIndex: 'serviceLineCapability',
      render: (_: string, record: NonResourcePricingTable) => (
        <Select allowClear value={record.serviceLineCapability || undefined} style={{ width: 150 }} onChange={(value) => handleSelectChange(value, record.key, 'serviceLineCapability')} disabled={viewOnly || !record.selectRegion}>
          {(record.selectRegion && options_[record.selectRegion]) ? Object.keys(options_[record.selectRegion]).sort().map((item: string) => (
            <Option key={`${record.key}-${item}-sl`} value={item}>{item}</Option>
          )) : []}
        </Select>
      ),
    },
    {
      title: 'Function',
      dataIndex: 'function',
      render: (_: string, record: NonResourcePricingTable) => (
        <Select allowClear value={record.function || undefined} style={{ width: 150 }} onChange={(value) => handleSelectChange(value, record.key, 'function')} disabled={viewOnly || !record.serviceLineCapability || !record.selectRegion}>
          {(record.serviceLineCapability && options_[record.selectRegion]?.[record.serviceLineCapability]) ? options_[record.selectRegion][record.serviceLineCapability].map((item: string) => (
            <Option key={`${record.key}-${item}-fn`} value={item}>{item}</Option>
          )) : []}
        </Select>
      ),
    },
    {
      title: (
        <span>
          Monthly Cost
          <Tooltip title="Loaded monthly cost for this non-resource line item">
            <InfoCircleOutlined style={{ marginLeft: '10px', color: '#4096FF' }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'monthlyCost',
      render: (text: number, record: NonResourcePricingTable) => (
        <InputNumber
          min={0}
          prefix={`${currencySymbol}`}
          value={record.monthlyCost}
          onChange={(value) => handleInputChange(value, record.key, 'monthlyCost')}
          style={{ width: 120, borderColor: 'blue' }}
          className="bold-input"
          disabled={viewOnly}
        />
      ),
    },
    {
      title: 'Target Client Margin %',
      dataIndex: 'targetClientMargin',
      render: (text: number, record: NonResourcePricingTable) => (
        <InputNumber
          formatter={(value) => ` ${value} %`}
          value={record.targetClientMargin}
          onChange={(value) => handleInputChange(value, record.key, 'targetClientMargin')}
          style={{ width: 100, borderColor: 'blue' }}
          className="bold-input"
          disabled={viewOnly}
        />
      ),
    },
    {
      title: 'Target Price / Month',
      dataIndex: 'targetPricePerMonth',
      render: (text: number) => (
        <InputNumber prefix={`${currencySymbol}`} value={Number(text?.toFixed(2))} disabled style={{ width: 120 }} className="bold-input" />
      ),
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      render: (date: Dayjs, record: NonResourcePricingTable) => (
        <DatePicker
          value={date > record.endDate.endOf('day') ? record.endDate.endOf('day') : date}
          allowClear={false}
          style={{ width: 130 }}
          onChange={(value) => handleInputChange(value, record.key, 'startDate')}
          disabledDate={(current) => {
            const startDateLimit = startDate.subtract(1, 'day').endOf('day');
            const endDateLimit = record.endDate.endOf('day');
            return current < startDateLimit || current > endDateLimit;
          }}
          disabled={viewOnly}
        />
      ),
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      render: (date: Dayjs, record: NonResourcePricingTable) => (
        <DatePicker
          value={date}
          allowClear={false}
          style={{ width: 130 }}
          onChange={(value) => handleInputChange(value, record.key, 'endDate')}
          disabledDate={(current) => {
            const startDateLimit = record.startDate.endOf('day');
            const endDateLimit = endDate.endOf('month').endOf('day');
            return current < startDateLimit || current > endDateLimit;
          }}
          disabled={viewOnly}
        />
      ),
    },
  ];

  const handleDelete = (key: number) => {
    const filteredData = [...dataSource.filter((item) => item.key !== key)];
    const newData = filteredData.map((item, index) => ({ ...item, key: index + 1 }));
    setDataSource(newData);
  };

  const handleAdd = () => {
    const maxKey = dataSource.length > 0 ? Math.max(...dataSource.map((item) => item.key)) : 0;
    const oldMaxKey = dataSource.length > 0 ? Math.max(...dataSource.map((item) => item.oldKey)) : 0;

    const newData: NonResourcePricingTable = {
      key: maxKey + 1,
      oldKey: oldMaxKey + 1,
      selectRegion: '',
      serviceLineCapability: '',
      function: '',
      monthlyCost: 0,
      targetClientMargin: 0,
      targetPricePerMonth: 0,
      startDate: startDate,
      endDate: endDate,
    };

    setDataSource([...dataSource, newData]);
  };

  const handleDuplicate = (duplicateRow: NonResourcePricingTable) => {
    const maxKey = dataSource.length > 0 ? Math.max(...dataSource.map((item) => item.key)) : 0;
    const oldMaxKey = dataSource.length > 0 ? Math.max(...dataSource.map((item) => item.oldKey)) : 0;

    const newData: NonResourcePricingTable = {
      ...duplicateRow,
      key: maxKey + 1,
      oldKey: oldMaxKey + 1,
    };

    setDataSource([...dataSource, newData]);
  };

  const handleSelectChange = (value: string, key: number, column: string) => {
    const order_ = ['selectRegion', 'serviceLineCapability', 'function'];
    const order_indx = order_.indexOf(column);
    let clear_values = {};
    for (let i = order_indx; i < order_.length; i++) {
      clear_values = { ...clear_values, [order_[i]]: '' };
    }

    const newData = [...dataSource];
    const index = newData.findIndex((item) => key === item.key);
    if (index > -1) {
      const item: NonResourcePricingTable = { ...newData[index], ...clear_values, [column]: value };
      if (item.selectRegion && item.serviceLineCapability && item.function) {
        const lookUpValues = nonResourcePricingDetailsData.filter((data: any) => data.region === item.selectRegion && data.service_line === item.serviceLineCapability && data.function === item.function)[0];
        item.monthlyCost = lookUpValues?.monthly_cost ?? 0;
        item.targetClientMargin = lookUpValues?.target_client_margin ? Number((lookUpValues.target_client_margin * 100).toFixed(2)) : 0;
        item.targetPricePerMonth = calcTargetPricePerMonth(item.monthlyCost, item.targetClientMargin);
      } else {
        item.monthlyCost = 0;
        item.targetClientMargin = 0;
        item.targetPricePerMonth = 0;
      }
      newData.splice(index, 1, item);
      setDataSource(newData);
    }
  };

  const handleInputChange = (value: any | null, key: number, column: string) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => key === item.key);
    if (index > -1) {
      const item: NonResourcePricingTable = { ...newData[index], [column]: value ?? (column === 'startDate' || column === 'endDate' ? newData[index][column as 'startDate' | 'endDate'] : 0) };
      item.targetPricePerMonth = calcTargetPricePerMonth(Number(item.monthlyCost) || 0, Number(item.targetClientMargin) || 0);
      newData.splice(index, 1, item);
      setDataSource(newData);
    }
  };

  return (
    <Card
      title={<>Non-Resource Costs <Tooltip title='Click on "Add Row" to add non-resource cost line items (monthly cost basis)'><InfoCircleOutlined style={{ marginLeft: '10px', marginTop: '2px', color: '#4096FF' }} /></Tooltip></>}
      style={{ marginBottom: '16px', boxShadow: '#8888881c 0px 0px 7px 7px', border: '2px solid #80808066' }}
    >
      <div style={{ width: '100%' }}>
        <Table
          dataSource={dataSource}
          columns={columns}
          tableLayout="auto"
          pagination={false}
          rowKey="oldKey"
          size="small"
          scroll={{ x: '100%' }}
          style={{ marginBottom: '16px' }}
          footer={() => (
            <Button type="dashed" onClick={handleAdd} icon={<PlusOutlined />} style={{ width: '100%' }} disabled={viewOnly}>
              Add Row
            </Button>
          )}
        />
      </div>
    </Card>
  );
};

export default NonResourceCosts;
